/* eslint-disable react/no-danger */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ArrowDownIcon,
  Box,
  Grid2,
  Popover,
  Show,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "components";
import dayjs from "dayjs";
import { FeeCardProps } from "interfaces/Revenue";
import { map, orderBy } from "lodash";
import { useState } from "react";
import { numberToCurrency } from "utils";

export const REVENUE_PRODUCT = [
  { name: "Management Fee", value: "management_fee" },
  { name: "Performance Fee", value: "performance_fee" },
  { name: "Subscription Fee", value: "subscription_fee" },
];

export const FEE_TYPE = [
  { name: "Flat Fee", value: "Flat Fee" },
  { name: "AUM Based", value: "AUM Based" },
];

export const BILLING_FREQUENCY = [
  { name: "Monthly", value: "Monthly" },
  { name: "Quarterly", value: "Quarterly" },
  { name: "Half Yearly", value: "Semi-Annually" },
  { name: "Yearly", value: "Yearly" },
];

export const FEE_PARAMETER = [
  { name: "Daily AUM", value: "AUM" },
  { name: "Trades", value: "Invested" },
  { name: "Cash Flow", value: "Cash Flow" },
  { name: "Daily AUM(excluding ledger balance)", value: "Current Value" },
  {
    name: "Daily AUM(excluding cash and cash equivalents)",
    value: "Daily AUM(excluding cash and cash equivalents)",
  },
  { name: "Opening AUM Current Value", value: "Opening AUM Current Value" },
  { name: "Opening AUM Portfolio Value", value: "Opening AUM Portfolio Value" },
  { name: "Month End Average", value: "Month End Portfolio Value Average" },
  { name: "Cash Flow Pro Rata", value: "Cash Flow Pro Rata" },
];

export const MANAGEMENT_CALCULATION_METHOD = [
  { name: "Advance", value: "Advance" },
  { name: "Trailing", value: "Trailing" },
  { name: "Advance Plus Trailing", value: "Advance Trailing" },
];

export const ADVANCE_TYPE = [
  { name: "Time period", value: "Time period" },
  { name: "Deductible", value: "Deductible" },
  { name: "Deposit", value: "Deposit" },
];

export const HURDLE_TYPE = [
  { name: "Hard", value: "Hard" },
  { name: "Soft", value: "Soft" },
];

export const SUBSCRIPTION_FREQUENCY = [
  { name: "Monthly", value: "Monthly" },
  { name: "Quarterly", value: "Quarterly" },
  { name: "Half Yearly", value: "Semi-Annually" },
  { name: "Yearly", value: "Yearly" },
];

export const PERFORMANCE_PLAN_FEE_PARAMETER = [
  { name: "Trades", value: "Invested" },
  { name: "Cash Flow", value: "Cash Flow" },
];

export const feeSections: {
  key: "managementFee" | "performanceFee" | "subscriptionFee";
  label: string;
}[] = [
  { key: "managementFee", label: "Management Fees" },
  { key: "performanceFee", label: "Performance Fees" },
  { key: "subscriptionFee", label: "Subscription Fees" },
];

export const FeeCard = ({ label, feeData }: FeeCardProps) => {
  if (!feeData) return null;

  return (
    <Grid2
      container
      spacing={"12px"}
      p={2}
      borderRadius={2}
      bgcolor={"var(--grey-bg-100)"}
    >
      <Grid2 size={12}>
        <Typography fontSize={13} fontWeight={500} color="var(--grey-700)">
          {label}
        </Typography>
      </Grid2>
      <Grid2 size={12}>
        <Typography
          fontSize={18}
          fontWeight={500}
          color="var(--black)"
          display={"flex"}
          alignItems={"center"}
          gap={0.5}
        >
          {numberToCurrency(feeData?.totalFee)}
          <Show.When isTrue={feeData.feeType !== "Flat Fee"}>
            <Typography
              fontWeight={500}
              color="var(--grey-700)"
              component={"span"}
            >
              ({feeData?.fee}%)
            </Typography>
          </Show.When>
        </Typography>
      </Grid2>
    </Grid2>
  );
};

export const Cell = styled(TableCell)({
  border: "1px solid var(--neutral-100) !important",
  borderBottom: "none",
  textTransform: "uppercase",
  background: "var(--neutral-50)",
  fontSize: "12px !important",
  fontWeight: "600 !important",
  color: "var(--black) !important",
});

export const TableBodyCell = styled(TableCell)({
  border: "1px solid var(--neutral-100) !important",
  background: "FFF",
  borderBottom: "1px solid var(--neutral-100) !important",
  fontSize: "14px !important",
  fontWeight: "500 !important",
  color: "var(--black) !important",
});

export const FeeAnnexureTable = ({ type, label, feeData }: FeeCardProps) => (
  <Accordion
    disableGutters
    square
    sx={{
      border: "1px solid var(--neutral-100)",
      boxShadow: "none",
      borderRadius: 2,
      "& .MuiButtonBase-root": {
        display: "flex",
        flexDirection: "row-reverse",
        gap: "12px",
      },
      "& .MuiAccordion-heading :hover": {
        bgcolor: "var(--neutral-50)",
      },
    }}
  >
    <AccordionSummary
      expandIcon={<ArrowDownIcon />}
      sx={{
        "& .MuiAccordionSummary-content": {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        },
      }}
    >
      <Typography fontWeight={500} color={"var(--black)"}>
        Annexure - {label}:
      </Typography>
      <Typography fontSize={18} fontWeight={500} color={"var(--black)"}>
        {numberToCurrency(feeData.totalFee)}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ p: 0 }}>
      <Box
        sx={{
          width: "-webkit-fill-available!important",
          overflow: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{
                "&.MuiTableRow-head th": { p: "12px" },
              }}
            >
              <Cell align="left">date period</Cell>
              {type !== "performanceFee" && (
                <Cell align="center">calculation</Cell>
              )}
              <Cell align="right">fees (₹)</Cell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(feeData?.annexure, (dataItem) => (
              <TableRow
                sx={{
                  "&.MuiTableRow-head th": { p: "12px" },
                }}
              >
                <TableBodyCell align="left" sx={{ width: "40%" }}>
                  <Typography fontWeight={500} color="var(--grey-900)">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: dataItem?.name,
                      }}
                    />
                  </Typography>
                  <Typography
                    fontSize={12}
                    fontWeight={500}
                    color="var(--neutral-500)"
                  >
                    {dataItem?.date}
                  </Typography>
                </TableBodyCell>
                {type !== "performanceFee" && (
                  <TableBodyCell align="center" sx={{ width: "40%" }}>
                    <Typography fontWeight={500} color="var(--grey-900)">
                      {dataItem?.calculation}
                    </Typography>
                  </TableBodyCell>
                )}
                <TableBodyCell align="right" sx={{ width: "20%" }}>
                  <Typography fontWeight={500}>{dataItem?.fee}</Typography>
                </TableBodyCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </AccordionDetails>
  </Accordion>
);

const generateFinancialYears = (startYear: number, endYear: number) => {
  const financialYears = [{ name: "All F.Y.", value: 0, dates: {} }];

  for (let year = startYear; year <= endYear; year++) {
    const nextYear = year + 1;
    financialYears.push({
      name: `${year}-${dayjs(`${nextYear}-03-31`).format("YY")}`,
      value: financialYears.length + 1,
      dates: {
        startDate: dayjs(`${year}-04-01`).format("YYYY-MM-DD"),
        endDate: dayjs(`${nextYear}-03-31`).format("YYYY-MM-DD"),
      },
    });
  }

  return financialYears;
};

const startYear = 2000;
const endYear = dayjs().get("year");

export const financialYearFilter = generateFinancialYears(startYear, endYear);

export function generateRandomString(length: number) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

interface Props {
  tableKey: string;
  updateFilter: (tableKey: string, val: any) => void;
  selectedValue: string;
}

export function FinancialYearDropdown(props: Props) {
  const { tableKey, updateFilter, selectedValue } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  return (
    <Grid2
      container
      spacing={1}
      display={"flex"}
      alignItems={"center"}
      justifyContent={"flex-end"}
    >
      <Grid2>
        <Typography fontSize={12} color="var(--neutral-700)">
          Financial Year:
        </Typography>
      </Grid2>
      <Grid2
        display={"flex"}
        alignItems={"center"}
        gap={0.5}
        sx={{
          cursor: "pointer",
          ":hover": {
            color: "var(--primary-900)",
          },
        }}
        onClick={(event: any) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        <Typography
          fontSize={13}
          fontWeight={500}
          color="var(--neutral-500)"
          sx={{ ":hover": { color: "var(--primary-700)" } }}
        >
          {selectedValue || "All F.Y."}
        </Typography>

        <ArrowDownIcon
          width={16}
          height={16}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "all 0.3s",
          }}
        />
      </Grid2>

      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: "85px",
              borderRadius: "8px",
              p: "4px 0",
              border: "1px solid var(--neutral-100)",
              bgcolor: "var(--white)",
              boxShadow: "4px 4px 12px 0px rgba(0, 0, 0, 0.25);",
            },
          },
        }}
      >
        <Grid2
          container
          minHeight={"200px"}
          maxHeight={"200px"}
          overflow={"auto"}
        >
          {map(orderBy(financialYearFilter, "name", "desc"), (item) => {
            return (
              <Grid2
                key={item.value}
                size={12}
                p={"8px 12px"}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  updateFilter(tableKey, item);
                  setAnchorEl(null);
                }}
              >
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  color="var(--grey-900)"
                >
                  {item.name}
                </Typography>
              </Grid2>
            );
          })}
        </Grid2>
      </Popover>
    </Grid2>
  );
}
